/**
 * Feature E07-F02 — "Data source strategy & adapters" request/response contracts.
 *
 * 1:1 port of the legacy Company Research prototype's Settings → Company Research
 * tab (../EER app/prototype/assets/research.data.js → MOCK.settings, and
 * research.proto.js → renderSettings/stratRow/setStrategy/setGate). Stage 2
 * (Company Research) existed in the old project ONLY as a prototype + a designed
 * DB schema (migration 023, mirrored here as Prisma model `data_source_settings`
 * / `research_requests.data_source`) — there was NO implemented business logic.
 * So this slice ports the prototype's contract verbatim and grounds it on the
 * real schema (docs/database-schema.md §3.2 + §3.5) and the unified error codes
 * (docs/architecture.md §4/§10, incl. STRATEGY_UNAVAILABLE).
 *
 * Endpoints honoured by the feature route (`routes/research-data-source.ts`):
 *   GET   /research/settings            → research_get_settings   (T06)
 *   PUT   /research/settings            → research_save_settings  (T06)
 *   GET   /research/strategies          → list strategies         (T01/T03/T06)
 *   POST  /research/strategies/:key/activate → setStrategy (one-active invariant, T03)
 *
 * Pluggable-provider parity (prototype renderSettings caption): "Pluggable
 * discovery/verification adapters. Exactly one active. Greyed = no credential."
 *   - exactly ONE strategy is_active at any time (T03);
 *   - a strategy with is_available=false cannot be activated (T03) and a run
 *     cannot start on it → error.code='STRATEGY_UNAVAILABLE' (T04);
 *   - the active strategy is SNAPSHOT into research_requests.data_source at run
 *     start so changing the global active later never rewrites run history (T05);
 *   - credentials_ref carries env-var NAMES only, never secret values (T01).
 *
 * Lives in its OWN file — the serial Wire pass adds the `export *` line to
 * `packages/shared/src/index.ts`; this agent never edits the barrel.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Adapter abstraction — {discover, enrich, verify} (T02)
// ---------------------------------------------------------------------------
/**
 * The three adapter capabilities every data-source strategy plugs into the
 * Company Research pipeline (T02 contract):
 *   - discover : find candidate companies (AI discovery, or ZoomInfo firmographic seed)
 *   - enrich   : attach firmographics (size, founded, hq, linkedin, …)
 *   - verify   : confirm relevance from first-party sources (About-Us via web_search)
 */
export const ADAPTER_CAPABILITIES = ['discover', 'enrich', 'verify'];
/**
 * The `adapters` JSONB on `data_source_settings` (§3.5: `{discover,enrich,verify}`).
 * Each value names the implementation handler the orchestrator dispatches to for
 * that capability (e.g. `ai`, `zoominfo`, `none`). Free-form string so a new
 * adapter implementation can be wired without a schema change.
 */
export const adapterMapSchema = z.object({
    discover: z.string().min(1),
    enrich: z.string().min(1),
    verify: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Strategy keys + the 4 seeded strategies (T01)
// ---------------------------------------------------------------------------
/**
 * The four data-source strategies seeded by T01 (research.data.js MOCK.settings
 * .strategies). `ai_discovery_verify` is the default ACTIVE + AVAILABLE one;
 * `zoominfo_first` is available (credential present); `apollo`/`crunchbase` are
 * stubs with is_available=false ("no access" / "pending").
 */
export const STRATEGY_KEYS = [
    'ai_discovery_verify',
    'zoominfo_first',
    'apollo',
    'crunchbase',
];
/** A single data-source strategy row, projected for the wire (read DTO). */
export const strategyDtoSchema = z.object({
    /** Stable provider key (UNIQUE in data_source_settings). */
    key: z.string().min(1),
    /** Human label for the radio row (display_name). */
    name: z.string().min(1),
    /** One-line description rendered under the radio. */
    description: z.string(),
    /** {discover,enrich,verify} handler map. */
    adapters: adapterMapSchema,
    /** env-var NAMES required by this strategy — never their values (T01 acceptance). */
    credentialsRef: z.array(z.string()),
    /** Exactly one strategy is `is_active:true` at any time (T03 invariant). */
    isActive: z.boolean(),
    /** Greyed/disabled in the UI when false (no credential / access pending). */
    isAvailable: z.boolean(),
});
// ---------------------------------------------------------------------------
// Review gate + weight profile (T06)
// ---------------------------------------------------------------------------
/** Review-gate mode (MOCK.settings.reviewGate). `gate` = mandatory human review; `auto` = auto-deliver. */
export const REVIEW_GATE_MODES = ['gate', 'auto'];
export const reviewGateModeSchema = z.enum(REVIEW_GATE_MODES);
/** A single weight-profile row (read-only default profile in the Settings preview). */
export const weightProfileEntrySchema = z.object({
    label: z.string().min(1),
    weight: z.number().int().min(0).max(100),
});
/**
 * The default weight profile (research.data.js MOCK.settings.weightProfile /
 * MOCK.weights; database-schema.md §3.6 seed `default`). Sum = 100. Returned
 * READ-ONLY in research_get_settings (T06 acceptance #4).
 */
export const DEFAULT_WEIGHT_PROFILE = [
    { label: 'Services match (About-Us)', weight: 35 },
    { label: 'Sub-industry', weight: 25 },
    { label: 'Geo (radius)', weight: 15 },
    { label: 'Company size', weight: 10 },
    { label: 'Founded window', weight: 10 },
    { label: 'Industry', weight: 5 },
];
// ---------------------------------------------------------------------------
// Discovery limits (E07-F02-T13) — how many companies the provider search reads
// ---------------------------------------------------------------------------
/**
 * E07-F02-T13 — the structured-provider (ZoomInfo) discovery reads several result
 * pages per fan-out tile until a ceiling. Two admin-editable ceilings, stored in the
 * active strategy row's `config.discoveryLimits` (like `reviewGate`) and so copied
 * into each run's data_source snapshot at run start:
 *   - `perTile`    — rows read for ONE tile (sub-industry × location × size band);
 *   - `perRequest` — distinct companies for the WHOLE request (all tiles together).
 * Free search, no AI — the ceilings bound volume/latency, not money.
 */
export const DISCOVERY_LIMIT_BOUNDS = {
    perTile: { min: 10, max: 1000 },
    perRequest: { min: 10, max: 5000 },
};
/** Defaults when the admin never set a value (wave-1b reconciliation: 50–350 per list). */
export const DEFAULT_DISCOVERY_LIMITS = { perTile: 200, perRequest: 1000 };
export const discoveryLimitsSchema = z.object({
    perTile: z
        .number()
        .int()
        .min(DISCOVERY_LIMIT_BOUNDS.perTile.min)
        .max(DISCOVERY_LIMIT_BOUNDS.perTile.max),
    perRequest: z
        .number()
        .int()
        .min(DISCOVERY_LIMIT_BOUNDS.perRequest.min)
        .max(DISCOVERY_LIMIT_BOUNDS.perRequest.max),
});
/**
 * Read `discoveryLimits` from a strategy `config` / snapshot `config` blob. Each
 * field falls back to its default on its own (a missing or out-of-range value never
 * voids the other). Pure.
 */
export function readDiscoveryLimits(config) {
    const raw = config && typeof config === 'object'
        ? config.discoveryLimits
        : undefined;
    const pick = (key) => {
        const parsed = discoveryLimitsSchema.shape[key].safeParse(raw?.[key]);
        return parsed.success ? parsed.data : DEFAULT_DISCOVERY_LIMITS[key];
    };
    return { perTile: pick('perTile'), perRequest: pick('perRequest') };
}
// ---------------------------------------------------------------------------
// research_get_settings response (T06)
// ---------------------------------------------------------------------------
/**
 * GET /research/settings — the admin Settings → Company Research tab state.
 * `strategies[]` carries is_active/is_available so the UI draws the radio list
 * (active = green badge, !available = greyed disabled w/ "no access"/"pending").
 */
export const researchSettingsResponseSchema = z.object({
    strategies: z.array(strategyDtoSchema),
    /** The currently active strategy_key (mirror of strategies.find(isActive).key). */
    activeStrategy: z.string().min(1),
    /** Mandatory review gate vs. auto-deliver. */
    reviewGate: reviewGateModeSchema,
    /** Read-only default weight profile preview. */
    weightProfile: z.array(weightProfileEntrySchema),
    /** E07-F02-T13 — provider-discovery ceilings (defaults when never set). */
    discoveryLimits: discoveryLimitsSchema,
});
// ---------------------------------------------------------------------------
// research_save_settings body + response (T06)
// ---------------------------------------------------------------------------
/**
 * PUT /research/settings body. Both fields optional — the caller may change the
 * active strategy and/or the review gate. At least one must be present.
 * weightProfile is intentionally absent (read-only default; T06 acceptance #4).
 */
export const researchSaveSettingsSchema = z
    .object({
    /** Activate this strategy (must be is_available=true, else STRATEGY_UNAVAILABLE). */
    activeStrategy: z.enum(STRATEGY_KEYS).optional(),
    /** Switch the review gate mode. */
    reviewGate: reviewGateModeSchema.optional(),
    /** E07-F02-T13 — change one or both provider-discovery ceilings. */
    discoveryLimits: discoveryLimitsSchema.partial().optional(),
})
    .refine((v) => v.activeStrategy !== undefined ||
    v.reviewGate !== undefined ||
    (v.discoveryLimits !== undefined && Object.keys(v.discoveryLimits).length > 0), { message: 'No settings to update' });
/** Response of research_save_settings — the persisted settings (same shape as GET). */
export const researchSaveSettingsResponseSchema = researchSettingsResponseSchema;
// ---------------------------------------------------------------------------
// list / activate strategy
// ---------------------------------------------------------------------------
/** GET /research/strategies — the strategy radio list alone. */
export const strategiesListResponseSchema = z.object({
    strategies: z.array(strategyDtoSchema),
    activeStrategy: z.string().min(1),
});
/** `:key` path param for activate — must be one of the seeded keys. */
export const strategyKeyParamSchema = z.object({
    key: z.enum(STRATEGY_KEYS),
});
// ---------------------------------------------------------------------------
// data_source snapshot written into research_requests at run start (T05)
// ---------------------------------------------------------------------------
/**
 * The snapshot of the active strategy copied into `research_requests.data_source`
 * at run start (T05). Carries strategy_key + adapters + config, and NEVER any
 * secret values (only credentials_ref env-var NAMES). The run pipeline reads THIS
 * snapshot, not the live global active row, so later strategy changes don't
 * rewrite history.
 */
export const dataSourceSnapshotSchema = z.object({
    strategyKey: z.string().min(1),
    adapters: adapterMapSchema,
    config: z.record(z.string(), z.unknown()).default({}),
    /** env-var NAMES only — never values (parity with credentials_ref). */
    credentialsRef: z.array(z.string()).default([]),
    /** ISO 8601 timestamp the snapshot was taken. */
    snapshotAt: z.string(),
});
//# sourceMappingURL=data-source-strategy.js.map